import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { fetchCancelHistory } from "../../../../../Redux/slice/features/cancelSlice";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";

const CancelTable = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth.user);
  const { data, status, error } = useSelector((state) => state.cancel);
  const userID = user?.customerId;

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    bookingRefNo: "",
  });
  const [filterError, setFilterError] = useState("");

  useEffect(() => {
    if (userID !== undefined) {
      const payload = userID === 1 ? {} : { INTCREATEDBY: userID };
      dispatch(fetchCancelHistory(payload));
    }
  }, [userID, dispatch]);
  const handleExcelExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(transformedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    XLSX.writeFile(workbook, "booking_history.xlsx");
  };
  const handleShow = useCallback(() => {
    if (
      filters.fromDate &&
      filters.toDate &&
      new Date(filters.fromDate) > new Date(filters.toDate)
    ) {
      setFilterError("From Date cannot be after To Date.");
      return;
    }
    setFilterError("");
    const payload = { ...filters, INTCREATEDBY: userID === 1 ? 0 : userID };
    dispatch(fetchCancelHistory(payload));
  }, [dispatch, userID, filters]);
  const formatDateSafely = (dateString) => {
    if (!dateString) return "N/A";
    let parsableDateString = dateString;
    if (dateString.length > 11 && dateString.indexOf(" ") === -1) {
      parsableDateString = `${dateString.substring(
        0,
        11
      )} ${dateString.substring(11)}`;
    }
    const date = new Date(parsableDateString);
    if (isNaN(date.getTime())) {
      console.error("Failed to parse date string:", dateString);
      return "Invalid Date";
    }
    return date.toLocaleDateString();
  };

  const transformedData = useMemo(() => {
    if (status !== "succeeded" || !Array.isArray(data)) return [];
    return data;
  }, [data, status]);

const getDeductionMessage = (row) => {
  if (!row?.Discount || !row?.TotalAmount)
    return "No deduction applied";

  const percent = ((row.Discount / row.TotalAmount) * 100).toFixed(0);

  let daysInfo = "";

  try {
    if (row?.CheckinDate && row?.BillDate) {
      const cancelDateStr = row.BillDate.split(" ")[0]; 
      const checkin = new Date(row.CheckinDate);
      const cancel = new Date(cancelDateStr);

      if (!isNaN(checkin) && !isNaN(cancel)) {
        const diffTime = checkin - cancel;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays >= 0) {
          daysInfo = ` (${diffDays} days before check-in)`;
        }
      }
    }
  } catch (err) {
    console.error("Deduction date calc error", err);
  }

  return `${percent}% deducted of ₹${row.TotalAmount} – cancellation policy applied${daysInfo}`;
};


const [visibleCols, setVisibleCols] = useState({
  bookingRef: true,
  hotel: true,
  customer: true,
  bookingDate: true,
  cancelDate: true,
  checkin: true,
  checkout: true,
  bookingAmount: true,
  tax: true,
  cancelAmount: true,
  refund: true,
  deduction: true,
});
const toggleCol = (key) => {
  setVisibleCols((prev) => ({
    ...prev,
    [key]: !prev[key],
  }));
};


const HeaderToggle = ({ label, colKey, visibleCols, toggleCol }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    {visibleCols[colKey] && <span>{label}</span>}
    <button
      onClick={() => toggleCol(colKey)}
      style={{
        fontSize: "11px",
        padding: "2px 6px",
        borderRadius: "4px",
        border: "1px solid #ccc",
        background: visibleCols[colKey] ? "#198754" : "#dc3545",
        color: "#fff",
        cursor: "pointer",
      }}
    >
      {visibleCols[colKey] ? "ON" : "OFF"}
    </button>
  </div>
);



 const allColumns = useMemo(() => [
  {
    key: "bookingRef",
    name: (
      <HeaderToggle
        label="Booking Ref No"
        colKey="bookingRef"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.BookingRefNo,
    sortable: true,
    minWidth: "180px",
  },
  {
    key: "hotel",
    name: (
      <HeaderToggle
        label="Hotel Name"
        colKey="hotel"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.PropertyName,
    sortable: true,
    minWidth: "180px",
  },
  {
    key: "customer",
    name: (
      <HeaderToggle
        label="Customer Name"
        colKey="customer"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.CustomerName,
    sortable: true,
    minWidth: "160px",
  },
  {
    key: "bookingDate",
    name: (
      <HeaderToggle
        label="Booking Date"
        colKey="bookingDate"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.IssueDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    key: "cancelDate",
    name: (
      <HeaderToggle
        label="Cancel Date"
        colKey="cancelDate"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.BillDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    key: "checkin",
    name: (
      <HeaderToggle
        label="Check-in"
        colKey="checkin"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.CheckinDate,
    minWidth: "150px",
  },
  {
    key: "checkout",
    name: (
      <HeaderToggle
        label="Check-out"
        colKey="checkout"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => row.CheckOutDate,
    sortable: true,
    minWidth: "160px",
  },
  {
    key: "bookingAmount",
    name: (
      <HeaderToggle
        label="Booking Amount"
        colKey="bookingAmount"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => `₹${row.TotalAmount?.toFixed(2) || "0.00"}`,
    sortable: true,
    minWidth: "180px",
  },
  {
    key: "tax",
    name: (
      <HeaderToggle
        label="Tax Amount"
        colKey="tax"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => `₹${row.TaxAmount?.toFixed(2) || "0.00"}`,
    sortable: true,
    minWidth: "150px",
  },
  {
    key: "cancelAmount",
    name: (
      <HeaderToggle
        label="Cancel Amount"
        colKey="cancelAmount"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => `₹${row.Discount?.toFixed(2) || "0.00"}`,
    sortable: true,
    minWidth: "170px",
  },
  {
    key: "refund",
    name: (
      <HeaderToggle
        label="Refund Amount"
        colKey="refund"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => `₹${row.Amount?.toFixed(2) || "0.00"}`,
    sortable: true,
    minWidth: "160px",
  },
  {
    key: "deduction",
    name: (
      <HeaderToggle
        label="Cancellation Deduction Details"
        colKey="deduction"
        visibleCols={visibleCols}
        toggleCol={toggleCol}
      />
    ),
    selector: row => getDeductionMessage(row),
    wrap: true,
    minWidth: "400px",
  },
], [visibleCols]);

const columns = useMemo(() => {
  return allColumns.filter(col => visibleCols[col.key]);
}, [allColumns, visibleCols]);


  const customStyles = {
    rows: { style: { minHeight: "45px" } },
    headCells: {
      style: {
        fontWeight: "bold",
        fontSize: "14px",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        position: "sticky",
        top: 0,
        zIndex: 1,
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: "clip",
      },
    },
    headRow: {
      style: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        backgroundColor: "#f8f9fa",
      },
    },
    cells: { style: { border: "1px solid #dee2e6", padding: "8px" } },
  };

  if (status === "loading") {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          {error || "Failed to fetch cancellation history"}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Cancel From Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, fromDate: e.target.value }))
                }
                max={filters.toDate || undefined}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Cancel To Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, toDate: e.target.value }))
                }
                min={filters.fromDate || undefined}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end gap-2">
              <button className="btn btn-primary w-100" onClick={handleShow}>
                <i className="bi bi-search me-2"></i>Show
              </button>
              <CSVLink
                data={transformedData}
                filename="booking_history.csv"
                className="btn btn-sm btn-outline-primary"
                title="Export CSV"
              >
                <i className="bi bi-file-earmark"></i>
              </CSVLink>
              <button
                className="btn btn-sm btn-outline-success"
                onClick={handleExcelExport}
                title="Export Excel"
              >
                <i className="bi bi-file-earmark-excel"></i>
              </button>
            </div>
          </div>

          {filterError && (
            <div className="row mt-2">
              <div className="col text-danger">{filterError}</div>
            </div>
          )}
        </div>
      </div>
      

      <DataTable
        columns={columns}
        data={transformedData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[50, 100, 500]}
        highlightOnHover
        striped
        persistTableHead
        customStyles={customStyles}
        noDataComponent="No cancellation history found."
        responsive
        fixedHeader
        fixedHeaderScrollHeight="500px"
      />

    </div>
  );
};  

export default React.memo(CancelTable);
